import { IPlacement, QuestionCategories, singleLineAddress } from "@sp-crm/core";
import { AiSummary } from "components/ai-summary/ai-summary";
import { RecentActivitiesCard } from "components/details-page-v-next/recent-activities";
import { ScheduledActivitiesCard } from "components/details-page-v-next/scheduled-activities";
import { Notes } from "components/notes/index";
import { NgGrid } from "components/shared/grid";
import { ActivityParent } from "components/visits/types";
import { WorkflowEntityExecutions } from "components/workflow/workflow-entity-executions";
import React from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useFeature, useResponsiveMode } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ClientAgreementSection } from "./client-agreement-section";
import { ClientAdditionalContacts, ClientBestContact } from "./client-best-contact";
import { ClientCurrentLocation } from "./client-current-location";
import { ClientInfoForm } from "./client-info-form";
import { PlacementReferralSource } from "./placement-referral-source";
import { LegacyPowerOfAttorney } from "./power-of-attorney";
import ClientQuestionSection from "./question-section";

interface PlacementOverviewTabProps {
    placement: IPlacement;
    onFieldChange: (fieldName: string, newValue: string) => void;
    onFieldChangeMulti: (fieldValues: Record<string, unknown>) => void;
    onSendDisclosure: () => void;
}

const clientActivityParent = (placement: IPlacement): ActivityParent => ({
    kind: "client",
    id: placement.id,
    name: placement.preferredClientName || placement.name || "(no name)",
    address: singleLineAddress(placement),
});

const PlacementOverviewTabVNext: React.FC<PlacementOverviewTabProps> = props => {
    const { placement, onFieldChange, onSendDisclosure } = props;
    const activityParent = clientActivityParent(placement);

    return (
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4 lg:items-start print:block">
            <div className="flex flex-col gap-2 lg:gap-4">
                <ScheduledActivitiesCard parent={activityParent} />
                <ClientAdditionalContacts clientId={placement.id} />
                <PlacementReferralSource
                    placement={placement}
                    onFieldChange={onFieldChange}
                />
                <ClientCurrentLocation
                    placement={placement}
                    onFieldChange={onFieldChange}
                />
                <LegacyPowerOfAttorney
                    placement={placement}
                    onFieldChange={onFieldChange}
                />
                <WorkflowEntityExecutions entity={placement} />
            </div>
            <div className="flex flex-col gap-2 lg:gap-4">
                <RecentActivitiesCard parent={activityParent} />
                <ClientQuestionSection
                    category={QuestionCategories.summary}
                    clientId={placement.id}
                />
                <ClientAgreementSection
                    placement={placement}
                    onFieldChange={onFieldChange}
                    onSendDisclosure={onSendDisclosure}
                />
            </div>
        </div>
    );
};

export const PlacementOverviewTab: React.FC<PlacementOverviewTabProps> = props => {
    const { placement, onFieldChange, onFieldChangeMulti, onSendDisclosure } = props;
    const {
        settings: { showClientFirst },
    } = useTenantSettings();
    const responsiveMode = useResponsiveMode();
    const isMobile = responsiveMode < ResponsiveMode.large;
    const detailsPageVNext = useFeature("detailsPageVNext");

    if (detailsPageVNext) {
        return <PlacementOverviewTabVNext {...props} />;
    }

    return (
        <NgGrid>
            <NgGrid.Column>
                {isMobile ? <Notes entityId={placement.id} entityType="Client" /> : null}
                {showClientFirst ? (
                    <>
                        <ClientInfoForm
                            placement={placement}
                            onFieldChange={onFieldChange}
                            onMultiFieldChange={onFieldChangeMulti}
                        />
                        <ClientCurrentLocation
                            placement={placement}
                            onFieldChange={onFieldChange}
                        />
                        <ClientBestContact
                            placement={placement}
                            onFieldChangeMulti={onFieldChangeMulti}
                        />
                    </>
                ) : (
                    <>
                        <ClientBestContact
                            placement={placement}
                            onFieldChangeMulti={onFieldChangeMulti}
                        />
                        <ClientInfoForm
                            placement={placement}
                            onFieldChange={onFieldChange}
                            onMultiFieldChange={onFieldChangeMulti}
                        />
                        <ClientCurrentLocation
                            placement={placement}
                            onFieldChange={onFieldChange}
                        />
                    </>
                )}
                <LegacyPowerOfAttorney
                    placement={placement}
                    onFieldChange={onFieldChange}
                />
            </NgGrid.Column>
            <NgGrid.Column>
                <AiSummary clientId={placement.id} />
                {isMobile ? null : <Notes entityId={placement.id} entityType="Client" />}
                <ClientQuestionSection
                    category={QuestionCategories.summary}
                    clientId={placement.id}
                />
                <PlacementReferralSource
                    placement={placement}
                    onFieldChange={onFieldChange}
                />
                <ClientAgreementSection
                    placement={placement}
                    onFieldChange={onFieldChange}
                    onSendDisclosure={onSendDisclosure}
                />
                <WorkflowEntityExecutions entity={placement} />
            </NgGrid.Column>
        </NgGrid>
    );
};
